import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Award, Users, BookOpen, ClipboardList, Flame, TrendingUp, Calendar, FileCheck } from "lucide-react";
import { api } from "../../lib/api";
import AssignmentFormModal from "./AssignmentFormModal";
import QuizBuilderModal from "./QuizBuilderModal";
import ResourcesPanel from "./ResourcesPanel";
import SubmissionsDrawer from "./SubmissionsDrawer";

/**
 * Group detail drawer — overview, members leaderboard, per-chapter averages,
 * and an assignments list with add / delete. Slides in from the right.
 */
function describeAssignmentScope(a) {
  if (a.type === "teacher_quiz" && a.questions) {
    const n = a.questions.length;
    return `${n} question${n !== 1 ? "s" : ""}`;
  }
  if (a.chapter_id) return `Chapter: ${a.chapter_id.toUpperCase()}`;
  return "Chapter not required";
}

export default function GroupDetailDrawer({ groupId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [buildingQuiz, setBuildingQuiz] = useState(false);
  const [viewingSubmissionsFor, setViewingSubmissionsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [a, c] = await Promise.all([
        api.get(`/staff/groups/${groupId}/analytics`),
        api.get("/chapters"),
      ]);
      setData(a.data);
      setChapters(c.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load group");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleDeleteAssignment = async (asn) => {
    if (!window.confirm(`Delete assignment "${asn.title}"?`)) return;
    await api.delete(`/staff/assignments/${asn.assignment_id}`);
    load();
    onChanged?.();
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="group-detail-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="absolute top-0 end-0 h-full w-full max-w-3xl bg-bone shadow-2xl flex flex-col border-s border-border">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Group</div>
            <div className="font-display text-xl font-semibold tracking-tight truncate" data-testid="group-detail-name">
              {data?.group?.name || "…"}
            </div>
            {data?.group?.description && (
              <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{data.group.description}</div>
            )}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-secondary text-muted-foreground" data-testid="group-detail-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {err && <div className="text-sm text-terracotta">{err}</div>}
          {loading && <div className="text-center text-sm text-muted-foreground">Loading…</div>}

          {data && (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryStat icon={Users} label="Students" value={data.summary.student_count} />
                <SummaryStat icon={ClipboardList} label="Assignments" value={data.summary.assignment_count} />
                <SummaryStat icon={Award} label="Group avg." value={data.summary.group_avg_quiz_percentage == null ? "—" : `${data.summary.group_avg_quiz_percentage}%`} />
                <SummaryStat icon={TrendingUp} label="Quiz tasks" value={data.summary.quiz_assignments} />
              </div>

              {/* Leaderboard */}
              <section data-testid="group-leaderboard">
                <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-terracotta" /> Leaderboard
                </h3>
                <div className="bg-white border border-border rounded-2xl overflow-hidden">
                  {data.leaderboard.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No quiz scores yet — students' averages will appear here.
                    </div>
                  )}
                  {data.leaderboard.map((m, i) => (
                    <div key={m.user_id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0" data-testid={`leaderboard-row-${m.user_id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? "bg-amber-100 text-amber-700" :
                        i === 1 ? "bg-slate-200 text-slate-700" :
                        i === 2 ? "bg-orange-100 text-orange-700" :
                        "bg-secondary text-muted-foreground"
                      }`}>
                        {i + 1}
                      </div>
                      {m.picture ? (
                        <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-sage/10 text-sage text-xs font-semibold flex items-center justify-center">
                          {(m.name || m.email || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                        <div className="text-[11px] text-muted-foreground">{m.quiz_best_count} chapters completed</div>
                      </div>
                      <div className="text-sm font-display font-semibold text-sage">{m.avg_quiz_percentage}%</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Members */}
              <section data-testid="group-members">
                <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-sage" /> Members ({data.members.length})
                </h3>
                <div className="bg-white border border-border rounded-2xl overflow-hidden">
                  {data.members.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">No members yet — edit the group to add students.</div>
                  )}
                  {data.members.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0" data-testid={`member-row-${m.user_id}`}>
                      {m.picture ? (
                        <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-sage/10 text-sage text-xs font-semibold flex items-center justify-center">
                          {(m.name || m.email || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                      </div>
                      <div className="hidden sm:block text-xs text-muted-foreground">
                        {m.chapters_opened}/{m.total_chapters} chapters · {m.quiz_attempts} attempts
                      </div>
                      <div className="text-xs">
                        {m.assigned_completion_pct == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold ${
                            m.assigned_completion_pct >= 80 ? "bg-sage/10 text-sage" :
                            m.assigned_completion_pct >= 40 ? "bg-amber-100 text-amber-700" :
                            "bg-terracotta/10 text-terracotta"
                          }`}>
                            {m.assigned_completion_pct}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Per-chapter averages */}
              <section>
                <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-sage" /> Per-chapter group average
                </h3>
                <div className="bg-white border border-border rounded-2xl p-4 space-y-2" data-testid="group-per-chapter">
                  {data.per_chapter.map((c) => (
                    <div key={c.chapter_id} className="flex items-center gap-3">
                      <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold w-12 shrink-0">
                        Ch.{c.chapter_number}
                      </div>
                      <div className="text-sm flex-1 truncate">{c.chapter_title}</div>
                      <div className="w-32 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div
                          className="h-full bg-sage transition-all"
                          style={{ width: `${c.avg_percentage || 0}%` }}
                        />
                      </div>
                      <div className="text-xs font-medium w-20 text-end">
                        {c.avg_percentage == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <>{c.avg_percentage}% <span className="text-[10px] text-muted-foreground">· {c.student_count_attempted}</span></>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Assignments */}
              <section data-testid="group-assignments-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-base font-semibold flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-terracotta" /> Assignments
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setBuildingQuiz(true)}
                      data-testid="group-build-quiz"
                      className="inline-flex items-center gap-1.5 bg-white border border-border hover:border-sage/50 hover:bg-sage/5 text-xs font-medium px-3 py-1.5 rounded-lg transition"
                    >
                      <Plus className="w-3.5 h-3.5" /> Custom quiz
                    </button>
                    <button
                      onClick={() => setCreatingAssignment(true)}
                      data-testid="group-add-assignment"
                      className="inline-flex items-center gap-1.5 bg-sage hover:bg-sage-dark text-white text-xs font-medium px-3 py-1.5 rounded-lg transition"
                    >
                      <Plus className="w-3.5 h-3.5" /> Quick add
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-border rounded-2xl divide-y divide-border">
                  {data.assignments.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No assignments yet. Create a chapter quiz, a custom quiz, homework, or a flashcard review.
                    </div>
                  )}
                  {data.assignments.map((a) => (
                    <div key={a.assignment_id} className="px-4 py-3 flex items-center gap-3" data-testid={`assignment-row-${a.assignment_id}`}>
                      <div className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        a.type === "quiz" ? "bg-sage/10 text-sage" :
                        a.type === "teacher_quiz" ? "bg-sage text-white" :
                        a.type === "flashcards" ? "bg-amber-100 text-amber-700" :
                        "bg-terracotta/10 text-terracotta"
                      }`}>
                        {a.type === "teacher_quiz" ? "custom quiz" : a.type}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {describeAssignmentScope(a)}
                          {a.due_date && (
                            <> · <Calendar className="w-2.5 h-2.5 inline" /> due {a.due_date}</>
                          )}
                        </div>
                      </div>
                      {(a.type === "homework" || a.type === "teacher_quiz") && (
                        <button
                          onClick={() => setViewingSubmissionsFor(a.assignment_id)}
                          data-testid={`assignment-submissions-${a.assignment_id}`}
                          className="inline-flex items-center gap-1 text-xs text-sage hover:text-sage-dark font-medium px-2 py-1 rounded-md hover:bg-sage/5"
                        >
                          <FileCheck className="w-3.5 h-3.5" /> Review
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteAssignment(a)}
                        data-testid={`assignment-delete-${a.assignment_id}`}
                        className="w-8 h-8 rounded-lg hover:bg-terracotta/10 hover:text-terracotta text-muted-foreground transition"
                      >
                        <Trash2 className="w-3.5 h-3.5 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              {/* Group resources library */}
              <ResourcesPanel scope="group" groupId={groupId} />
            </>
          )}
        </div>
      </aside>

      {creatingAssignment && (
        <AssignmentFormModal
          groupId={groupId}
          chapters={chapters}
          onClose={() => setCreatingAssignment(false)}
          onSaved={() => { setCreatingAssignment(false); load(); onChanged?.(); }}
        />
      )}

      {buildingQuiz && (
        <QuizBuilderModal
          groupId={groupId}
          onClose={() => setBuildingQuiz(false)}
          onSaved={() => { setBuildingQuiz(false); load(); onChanged?.(); }}
        />
      )}

      {viewingSubmissionsFor && (
        <SubmissionsDrawer
          assignmentId={viewingSubmissionsFor}
          onClose={() => setViewingSubmissionsFor(null)}
        />
      )}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-xl p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-sage/10 text-sage flex items-center justify-center">
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold truncate">{label}</div>
        <div className="font-display text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}
